import {useEffect} from 'react';

// Termly prepends this container to <body> and renders the consent prompt inside it.
const TERMLY_CONTAINER_ID = 'termly-code-snippet-support';
const CSS_VARIABLE = '--cookie-banner-offset';
// Marker on <html> so stylesheets can react to the prompt being up, not just to its height.
const VISIBLE_ATTRIBUTE = 'data-cookie-banner';

// Anything within this many pixels of the viewport bottom counts as sitting on the bottom edge.
const BOTTOM_EDGE_TOLERANCE = 2;

/**
 * Publishes how much of the viewport bottom the cookie consent prompt covers as
 * `--cookie-banner-offset` on <html>, plus a `data-cookie-banner="visible"` marker
 * while it is up, so fixed-bottom UI (the nav buttons, the HubSpot chat bubble) can
 * sit above it instead of underneath.
 *
 * Termly exposes no height and hashes its class names per release, so we measure
 * the rendered geometry instead: any fixed element it renders that reaches the
 * bottom edge counts. Full-viewport overlays (the preferences modal) are skipped
 * since they cover the page regardless of where we move things.
 */
const useCookieBannerOffset = (): void => {
    useEffect(() => {
        const measure = (): number => {
            const container = document.getElementById(TERMLY_CONTAINER_ID);
            if (!container) return 0;

            const viewportHeight = window.innerHeight;
            const queue: Element[] = Array.from(container.children);
            let offset = 0;

            for (let i = 0; i < queue.length; i++) {
                const element = queue[i];

                const style = window.getComputedStyle(element);

                if (style.position !== 'fixed') {
                    queue.push(...Array.from(element.children));
                    continue;
                }

                const rect = element.getBoundingClientRect();
                // `display: none` collapses the rect to zero, but `visibility: hidden` keeps the
                // layout box — without this an invisible element would still push the nav bar up.
                const isHidden = style.visibility === 'hidden' || rect.width === 0 || rect.height === 0;
                const coversViewport = rect.top <= 0;
                const reachesBottomEdge = rect.bottom >= viewportHeight - BOTTOM_EDGE_TOLERANCE;

                if (isHidden || coversViewport || !reachesBottomEdge) continue;

                offset = Math.max(offset, viewportHeight - rect.top);
            }

            return Math.round(offset);
        };

        let frame = 0;
        const schedule = () => {
            if (frame) return;
            frame = window.requestAnimationFrame(() => {
                frame = 0;
                const offset = measure();
                const root = document.documentElement;

                root.style.setProperty(CSS_VARIABLE, `${offset}px`);

                if (offset > 0) {
                    root.setAttribute(VISIBLE_ATTRIBUTE, 'visible');
                } else {
                    root.removeAttribute(VISIBLE_ATTRIBUTE);
                }
            });
        };

        let observed: Element | null = null;
        const promptObserver = new MutationObserver(schedule);
        const watchPrompt = () => {
            const container = document.getElementById(TERMLY_CONTAINER_ID);
            if (container === observed) return;

            promptObserver.disconnect();
            observed = container;

            if (container) {
                promptObserver.observe(container, {
                    attributeFilter: ['class', 'style'],
                    attributes: true,
                    childList: true,
                    subtree: true,
                });
            }
        };

        // Termly loads asynchronously, so watch <body> until its container shows up.
        const bodyObserver = new MutationObserver(() => {
            watchPrompt();
            schedule();
        });
        bodyObserver.observe(document.body, {childList: true});

        window.addEventListener('resize', schedule);
        watchPrompt();
        schedule();

        return () => {
            if (frame) window.cancelAnimationFrame(frame);
            bodyObserver.disconnect();
            promptObserver.disconnect();
            window.removeEventListener('resize', schedule);
            document.documentElement.style.removeProperty(CSS_VARIABLE);
            document.documentElement.removeAttribute(VISIBLE_ATTRIBUTE);
        };
    }, []);
};

export default useCookieBannerOffset;
